import { useMemo } from 'react';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import type { CustomAttributeSchemaLookup } from 'src/pages/custom-datasources/update/types';

import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import { useCustomAttributeLookup } from '../../custom-data-source-widget/useCustomAttributeLookup';
import type { SettingsSchema } from '../settingsSchema';
import type { GigawidgetSettings } from '../util';
import { UniversalWidgetSettingsForm } from './UniversalWidgetSettingsForm';

type Props = {
  onDismiss: () => void;
  onSave: (
    data: SettingsSchema,
    customAttributeFieldLabels?: Record<string, string>
  ) => Promise<void>;
};

/**
 * Friendly labels for every custom-attribute field, keyed by attribute id, so
 * the auto-generated widget title can resolve a Number aggregation field to its
 * label instead of the raw attribute id. The id used here (`renderProps.path`)
 * matches the column id used as `aggregationField` in settings.
 */
const getCustomAttributeFieldLabels = (
  lookup: CustomAttributeSchemaLookup | undefined
): Record<string, string> => {
  const labels: Record<string, string> = {};
  if (!lookup) {
    return labels;
  }
  for (const schema of Object.values(lookup)) {
    if (!schema?.Schema || !schema?.UiSchema) {
      continue;
    }
    for (const field of getCustomAttributeRenderProps(
      schema.Schema,
      schema.UiSchema
    )) {
      labels[field.path] = field.label;
    }
  }

  return labels;
};

export const UniversalWidgetSettingsModal = ({ onDismiss, onSave }: Props) => {
  const [settings] = useDashboardWidgetSettings<GigawidgetSettings>();
  const { customAttributeSchemaLookup } = useCustomAttributeLookup();
  const customAttributeFieldLabels = useMemo(
    () => getCustomAttributeFieldLabels(customAttributeSchemaLookup),
    [customAttributeSchemaLookup]
  );

  return (
    <UniversalWidgetSettingsForm
      renderTemplate={(renderProps) => (
        <ModalWrapper {...renderProps} visible={true} />
      )}
      settings={settings}
      onSave={(data) => onSave(data, customAttributeFieldLabels)}
      onDismiss={onDismiss}
    />
  );
};
